import type {
	NotificationQuery,
	NotificationsResponse,
	User,
	UserRegistration,
} from '@notify-x/shared';

export class ApiClient {
	private baseUrl: string;

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl;
	}

	/**
	 * Register a new user or get existing user
	 */
	async registerUser(registration: UserRegistration): Promise<User> {
		const response = await fetch(`${this.baseUrl}/api/users/register`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(registration),
		});

		if (!response.ok) {
			throw new Error(`Failed to register user: ${response.statusText}`);
		}

		const result = await response.json<{ success: boolean; data: { user: User } }>();

		if (!result.success || !result.data?.user) {
			throw new Error('Invalid response from server');
		}

		return result.data.user;
	}

	/**
	 * Update user's FCM token
	 */
	async updateFcmToken(userId: number, fcmToken: string): Promise<void> {
		const response = await fetch(`${this.baseUrl}/api/users/${userId}/fcm-token`, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ fcm_token: fcmToken }),
		});

		if (!response.ok) {
			throw new Error(`Failed to update FCM token: ${response.statusText}`);
		}
	}

	/**
	 * Get notifications for a user
	 */
	async getNotifications(query: NotificationQuery): Promise<NotificationsResponse> {
		const params = new URLSearchParams({
			user_id: query.user_id.toString(),
		});

		if (query.limit) {
			params.append('limit', query.limit.toString());
		}

		if (query.offset) {
			params.append('offset', query.offset.toString());
		}

		const response = await fetch(`${this.baseUrl}/api/notifications?${params.toString()}`);

		if (!response.ok) {
			throw new Error(`Failed to get notifications: ${response.statusText}`);
		}

		const result = await response.json<{ success: boolean; data: NotificationsResponse }>();

		if (!result.success || !result.data) {
			throw new Error('Invalid response from server');
		}

		return result.data;
	}

	/**
	 * Health check
	 */
	async healthCheck(): Promise<boolean> {
		try {
			const response = await fetch(`${this.baseUrl}/api/health`);
			return response.ok;
		} catch {
			return false;
		}
	}
}
